
import { ASPSP } from "@/types/openpayments/bank";
import { handleError } from "@/utils/handleError";
import { BankInfoApiClient } from "../clients/bankInfoApiClient";
import type { BankConstraintInfo } from "@/types/bankConstraints";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export class BankInfoService {
  private bankCache: Map<string, ASPSP[]> = new Map();

  constructor() {
  }

  /**
   * List all banks for a given country code (ISO 3166-1 alpha-2)
   * @param countryCode string
   * @returns Promise<ASPSP[]>
   */
  async listBanksByCountry(countryCode: string): Promise<ASPSP[]> {
    try {
      // Try to get from cache first
      if (this.bankCache.has(countryCode)) {
        return this.bankCache.get(countryCode) || [];
      }
      
      // First try to get banks from the edge function
      const edgeResponse = await BankInfoApiClient.getBanks(countryCode);
      if (edgeResponse && edgeResponse.banks && edgeResponse.banks.length > 0) {
        // Convert to ASPSP format for compatibility. ref/countryCode, not just bicFi/name/logoUrl
        // (FIN-783): the edge function has returned both since FIN-592/FIN-783 landed, but this
        // map silently dropped them, so every bank the consultant picker offered lost the
        // identity a bic-less bank -- or a bic shared by several institutions -- needs downstream.
        // psuTypes (FIN-1567) is the same shape of gap: present on the edge function response
        // since PR2, dropped here until now.
        const aspsps: ASPSP[] = edgeResponse.banks.map(bank => ({
          bicFi: bank.bicFi,
          name: bank.name,
          logoUrl: bank.logoUrl,
          countryCode: bank.countryCode,
          ref: bank.ref,
          psuTypes: bank.psuTypes,
        }));
        // Cache the results from edge function
        this.bankCache.set(countryCode, aspsps);
        return aspsps;
      }
      throw new Error(translateOutsideReact('consultantErrors.fallback.failedToLoadBanksList'));
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToLoadBanksList'), "listBanksByCountry");
      return [];
    }
  }
  
  /**
   * Get bank information by BIC code
   * @param bicFi string
   * @param countryCode string Optional country code to narrow search
   * @returns Promise<ASPSP | undefined>
   */
  async getBankByBic(bicFi: string, countryCode?: string): Promise<ASPSP | undefined> {
    try {
      // If we have a country code and it's cached, check cache first
      if (countryCode && this.bankCache.has(countryCode)) {
        const banks = this.bankCache.get(countryCode);
        const bank = banks?.find(bank => bank.bicFi === bicFi);
        if (bank) return bank;
      }

      // Try to get from edge function
      const edgeResponse = await BankInfoApiClient.getBankByBic(bicFi, countryCode);
      if (edgeResponse && edgeResponse.bank) {
        return edgeResponse.bank;
      }

      // Try common countries if no country code provided
      const countriesToTry = countryCode ? [countryCode] : ['SE', 'NO', 'FI', 'DK', 'DE', 'GB'];

      for (const country of countriesToTry) {
        const banks = await this.listBanksByCountry(country);
        const bank = banks.find(bank => bank.bicFi === bicFi);
        if (bank) return bank;
      }

      return undefined;
    } catch (error) {
      console.error("Error fetching bank by BIC:", error);
      return undefined;
    }
  }

  /**
   * Get bank constraints for a BIC — returns null if not in the banks table.
   * Errors are swallowed; callers treat null as "no constraint".
   */
  async getConstraint(bic: string): Promise<BankConstraintInfo | null> {
    return BankInfoApiClient.getConstraint(bic);
  }

  /**
   * Get list of supported countries
   * @returns Promise<Array<{ code: string, name: string }>>
   */
  async getSupportedCountries(): Promise<Array<{ code: string; name: string }>> {
    try {
      const response = await BankInfoApiClient.getSupportedCountries();
      return response.countries || [];
    } catch (error) {
      console.error("Error fetching supported countries:", error);
      // Return default countries as fallback
      return [
        { code: 'SE', name: 'Sweden' },
        { code: 'NO', name: 'Norway' },
        { code: 'DK', name: 'Denmark' }
      ];
    }
  }
}
